import React, { useEffect, useMemo, useRef } from 'react';
import { useState } from 'react';
import { useCallback } from 'react';
import Select from 'react-select';
import _ from 'lodash';
import { components } from 'react-select/lib/components';
import { useDebounce } from 'use-hooks';
import { SelectDropdownIndicator, AutocompleteControlComponent } from './Autocomplete';
import { SelectMenuPortal, defaultStyles } from './BasicSelect';
export const ReactSelectComponents = components;
const DefaultOptionComponent = (props) => React.createElement(ReactSelectComponents.Option, Object.assign({}, props));
const LoadingResults = ({ noResultsFound }) => {
    return useMemo(() => {
        if (noResultsFound) {
            return React.createElement("div", { className: `option select__option` }, "Symbol not found");
        }
        return (React.createElement("div", { className: `option select__option` }, "Loading...") //TODO: add in loading icon when component memos correctly <i className="fas fa-circle-notch fa-spin mx-2" />
        );
    }, [noResultsFound]);
};
export const AsyncAutocomplete = props => {
    const { OptionComponent, defaultValue, dropdownIcon, placeholder, search, onSelect, debounceTimeout, minimumLength, initialValues, isClearable, clearOptionsOnSelect, value, name, isSearchable, onClear, symbol, onBlur, charLimit, rest, rootPortalId } = props;
    const Option = useMemo(() => OptionComponent || DefaultOptionComponent, [OptionComponent]);
    const selectRef = useRef();
    const [noResultsFound, setNoResultsFound] = useState(false);
    const [queryValue, setQueryValue] = useState();
    const [inputValue, setInputValue] = useState('');
    const [, setIsOpen] = useState(false);
    const [minLength] = useState(_.isNumber(minimumLength) ? minimumLength : 2);
    const initialSelectOpts = useMemo(() => {
        return _(initialValues || [])
            .union([value])
            .filter(v => v !== undefined)
            .uniqBy(v => v.value)
            .value();
    }, [value, initialValues]);
    const [selectOptions, setOptions] = useState(initialSelectOpts);
    const setSelectOptions = useCallback((values) => {
        return setOptions(values);
    }, []);
    const debouncedQueryValue = useDebounce(queryValue, debounceTimeout || 175);
    useEffect(() => {
        setSelectOptions(initialValues);
    }, [initialValues]);
    useEffect(() => {
        if (debouncedQueryValue && debouncedQueryValue.length >= minLength) {
            search(debouncedQueryValue).then(res => {
                setSelectOptions(res);
                if (res.length <= 0) {
                    setNoResultsFound(true);
                }
            });
        }
    }, [debouncedQueryValue]);
    const onChange = useCallback((val, { action }) => {
        onSelect(val);
        setInputValue('');
        const clearOptions = clearOptionsOnSelect && action === 'select-option';
        if (clearOptions ||
            action === 'clear' ||
            action === 'deselect-option' ||
            action === 'remove-value' ||
            action === 'pop-value') {
            setSelectOptions(initialValues || []);
        }
        if (action === 'clear' && onClear) {
            onClear();
        }
    }, [onSelect]);
    const onInputChange = useCallback((val, { action }) => {
        switch (action) {
            case 'input-change': {
                setInputValue(val);
                setSelectOptions(initialValues || []);
                setQueryValue(val);
                setNoResultsFound(false);
                return;
            }
            case 'menu-close':
                if (selectOptions && selectOptions.length === 0 && !_.isNil(inputValue) && !_.isEmpty(inputValue)) {
                    setInputValue(symbol);
                    setInputValue('');
                    setSelectOptions(initialValues || []);
                }
                return;
            default:
                return;
        }
    }, [setIsOpen, inputValue, setInputValue, search]);
    const getOptionValue = useCallback((option) => {
        const optionVal = _.join([_.get(option, 'label'), _.get(option, 'searchValue'), _.get(option, 'value')], ' ');
        return optionVal;
    }, []);
    const currentValue = useMemo(() => {
        if (_.isNil(value) && !_.isNil(defaultValue)) {
            const initValue = _.find(selectOptions, o => o.data === defaultValue.data);
            return initValue;
        }
        return value || null;
    }, [defaultValue, value, selectOptions]);
    const formatOptionLabel = useCallback((option, labelMeta) => {
        if (!_.isNil(defaultValue) &&
            _.isNil(value) &&
            currentValue &&
            currentValue.value === option.value &&
            labelMeta.context === 'value') {
            return React.createElement("div", { className: `badge select__placeholder` }, placeholder);
        }
        return option.label;
    }, [currentValue, defaultValue, placeholder, value]);
    const styles = useMemo(() => {
        return {
            menuPortal: defaultStyles.menuPortal,
        };
    }, [window.scrollY, defaultStyles.menuPortal]);
    useEffect(() => {
        if (symbol && inputValue !== '' && symbol !== inputValue.toUpperCase()) {
            setInputValue(symbol);
            setSelectOptions(initialValues || []);
        }
    }, [symbol]);
    const inputFormat = (inputValue) => {
        if (charLimit) {
            return inputValue.slice(0, charLimit);
        }
        return inputValue;
    };
    return (React.createElement(Select, Object.assign({}, (rest || {}), { ref: selectRef, placeholder: placeholder, value: currentValue, inputValue: inputFormat(inputValue), onInputChange: onInputChange, menuPlacement: 'auto', formatOptionLabel: formatOptionLabel, onChange: onChange, name: name, isClearable: _.isBoolean(isClearable) ? isClearable : true, isSearchable: _.isBoolean(isSearchable) ? isSearchable : true, options: selectOptions, className: "basic-multi-select", getOptionValue: getOptionValue, classNamePrefix: "select", menuShouldBlockScroll: 'true', styles: styles, menuPortalTarget: document.getElementById(rootPortalId ? rootPortalId : 'portal-root'), noOptionsMessage: e => (e.inputValue ? '' : null), onKeyDown: e => {
            if (e.key === 'Enter') {
                search(inputValue, 1).then(res => {
                    if (res.length > 0 && res[0].value && res[0].value.toUpperCase() === inputValue.toUpperCase()) {
                        onChange(res[0], { action: 'set-value' });
                        if (selectRef && selectRef.current) {
                            selectRef.current.blur();
                        }
                    }
                });
            }
        }, components: {
            DropdownIndicator: rootPortalId ? undefined : SelectDropdownIndicator(dropdownIcon || 'fa-arrow-right'),
            Control: AutocompleteControlComponent,
            MenuPortal: SelectMenuPortal,
            Option,
            NoOptionsMessage: () => {
                return React.createElement(LoadingResults, { noResultsFound: noResultsFound });
            },
        }, onBlur: onBlur })));
};
//# sourceMappingURL=AsyncAutocomplete.js.map